import _ from 'lodash';
import React, {FunctionComponent} from 'react';
import {Grid, Button, ThemeProvider} from '@material-ui/core';
import {createTheme} from '@material-ui/core/styles';
import {CheckSharp} from '@material-ui/icons';
import {makeStyles} from '@material-ui/styles';
import {ProductData, ProductField, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {useSelector, useDispatch} from 'react-redux';
import {TreezColor, treezColorMap} from '~/styles/constants';
import {getFieldSelector, selectProductFieldSources} from '~/redux/selectors/mergeSelectors';
import {formatNumberToPrecision} from '~/lib/format';
import {removeProductSource, updateProductSource} from '~/redux/actions/MergeActions';
import {flattenProduct} from '~/lib/product';

const {GREY_LIGHT, GREEN_HIGHLIGHT, TREEZ_GREEN} = TreezColor;

interface Props {
    field: ProductData;
    disabled?: boolean;
    product: ProductInCatalog;
}

export const ProductGridCell: FunctionComponent<Props> = (props: Props) => {
    const {field, disabled = false, product} = props;

    const flattenedproduct = flattenProduct(product);

    const value = flattenedproduct[field];

    const fieldSelector = getFieldSelector(field) as (state: any) => any;

    if (fieldSelector == null) {
        console.error(
            `${field} did did not render appropriate selector for merge state`,
            fieldSelector
        );
    }

    const valueToCompare = useSelector(fieldSelector, _.isEqual);
    const valueIsEqualToMergeProductValue =
        _.isEqual(valueToCompare, value) && valueToCompare != null && valueToCompare != '';

    const productSources = useSelector(selectProductFieldSources);

    const dispatch = useDispatch();

    const isSelected = productSources[field] === product || valueIsEqualToMergeProductValue;

    const handleClick = () => {
        if (isSelected) {
            dispatch(removeProductSource(field));
        } else {
            dispatch(updateProductSource(field, product));
        }
    };

    const backgroundColor = isSelected ? treezColorMap[GREEN_HIGHLIGHT] : 'none';

    const useStyles = makeStyles({
        checkIcon: {
            alignItems: 'center',
            border: `1px solid ${treezColorMap[TREEZ_GREEN]}`,
            borderRadius: '50%',
            display: 'flex',
            height: '20px',
            justifyContent: 'center',
            width: '20px',
        },
        singleProductValue: {
            backgroundColor,
            borderLeft: `1px solid ${treezColorMap[GREY_LIGHT]}`,
            display: 'flex',
            height: '80px',
            maxWidth: '100%',
            width: '100%',
        },
        selectedIndicatorContainer: {
            display: 'flex',
            height: '20px',
            justifyContent: 'flex-end',
            width: '100%',
        },
        textContainer: {
            width: '100%',
        },
        valueButton: {
            margin: 'auto',
        },
        valueContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            width: '100%',
        },
    });

    const {
        checkIcon,
        singleProductValue,
        selectedIndicatorContainer,
        valueButton,
        valueContainer,
        textContainer,
    } = useStyles();

    const theme = createTheme({
        typography: {
            button: {
                textTransform: 'none',
            },
        },
        palette: {
            primary: {
                main: `${treezColorMap[TREEZ_GREEN]}`,
            },
        },
    });

    const valueToRender = field === ProductField.AMOUNT ? formatNumberToPrecision(value, 2) : value;

    return (
        <ThemeProvider theme={theme}>
            <Grid item className={singleProductValue}>
                <Button className={valueButton} disabled={disabled} onClick={handleClick}>
                    <Grid className={valueContainer}>
                        {isSelected && (
                            <Grid className={selectedIndicatorContainer}>
                                <div className={checkIcon}>
                                    <CheckSharp color="primary" />
                                </div>
                            </Grid>
                        )}
                        <Grid className={textContainer}>{valueToRender || ''}</Grid>
                    </Grid>
                </Button>
            </Grid>
        </ThemeProvider>
    );
};

export default React.memo(ProductGridCell);
